import { create } from 'zustand'

export type TaskType = 'scan' | 'execution'
export type TaskStatus = 'running' | 'success' | 'error'

export interface StreamEvent {
  type: string
  content: string
  timestamp: string
  data?: Record<string, unknown>
}

export interface BackgroundTask {
  id: string
  type: TaskType
  title: string
  subtitle?: string
  status: TaskStatus
  events: StreamEvent[]
  eventSource: EventSource | null
  createdAt: Date
  completedAt?: Date
  // Task-specific data
  metadata?: {
    workflowId?: number
    executor?: string
    scanner?: string
    mode?: string
    permissionMode?: string
  }
}

interface BackgroundTaskState {
  tasks: Map<string, BackgroundTask>
  minimizedTaskIds: Set<string>
  activeModalTaskId: string | null
  
  // Actions
  addTask: (task: Omit<BackgroundTask, 'events' | 'createdAt'>) => void
  updateTaskStatus: (id: string, status: TaskStatus) => void
  addTaskEvent: (id: string, event: StreamEvent) => void
  setTaskEventSource: (id: string, eventSource: EventSource | null) => void
  removeTask: (id: string) => void
  minimizeTask: (id: string) => void
  restoreTask: (id: string) => void
  setActiveModal: (id: string | null) => void
  getTask: (id: string) => BackgroundTask | undefined
  getRunningTasks: () => BackgroundTask[]
  getAllTasks: () => BackgroundTask[]
}

export const useBackgroundTaskStore = create<BackgroundTaskState>((set, get) => ({
  tasks: new Map(),
  minimizedTaskIds: new Set(),
  activeModalTaskId: null,

  addTask: (task) => set((state) => {
    const newTasks = new Map(state.tasks)
    newTasks.set(task.id, {
      ...task,
      events: [],
      createdAt: new Date(),
    })
    return { 
      tasks: newTasks,
      activeModalTaskId: task.id,
      minimizedTaskIds: new Set([...state.minimizedTaskIds].filter(id => id !== task.id))
    }
  }),

  updateTaskStatus: (id, status) => set((state) => {
    const task = state.tasks.get(id)
    if (!task) return state
    
    const newTasks = new Map(state.tasks)
    newTasks.set(id, {
      ...task,
      status,
      completedAt: status !== 'running' ? new Date() : undefined,
    })
    return { tasks: newTasks }
  }),

  addTaskEvent: (id, event) => set((state) => {
    const task = state.tasks.get(id)
    if (!task) return state
    
    const newTasks = new Map(state.tasks)
    newTasks.set(id, {
      ...task,
      events: [...task.events, event],
    })
    return { tasks: newTasks }
  }),

  setTaskEventSource: (id, eventSource) => set((state) => {
    const task = state.tasks.get(id)
    if (!task) return state
    
    const newTasks = new Map(state.tasks)
    newTasks.set(id, { ...task, eventSource })
    return { tasks: newTasks }
  }),

  removeTask: (id) => set((state) => {
    const task = state.tasks.get(id)
    if (task?.eventSource) {
      task.eventSource.close()
    }
    
    const newTasks = new Map(state.tasks)
    newTasks.delete(id)
    
    const newMinimized = new Set(state.minimizedTaskIds)
    newMinimized.delete(id)
    
    return { 
      tasks: newTasks,
      minimizedTaskIds: newMinimized,
      activeModalTaskId: state.activeModalTaskId === id ? null : state.activeModalTaskId
    }
  }),

  minimizeTask: (id) => set((state) => {
    const newMinimized = new Set(state.minimizedTaskIds)
    newMinimized.add(id)
    return { 
      minimizedTaskIds: newMinimized,
      activeModalTaskId: state.activeModalTaskId === id ? null : state.activeModalTaskId
    }
  }),

  restoreTask: (id) => set((state) => {
    const newMinimized = new Set(state.minimizedTaskIds)
    newMinimized.delete(id)
    return { 
      minimizedTaskIds: newMinimized,
      activeModalTaskId: id
    }
  }),

  setActiveModal: (id) => set((state) => {
    if (id) {
      const newMinimized = new Set(state.minimizedTaskIds)
      newMinimized.delete(id)
      return { activeModalTaskId: id, minimizedTaskIds: newMinimized }
    }
    return { activeModalTaskId: id }
  }),

  getTask: (id) => get().tasks.get(id),
  
  getRunningTasks: () => [...get().tasks.values()].filter(t => t.status === 'running'),
  
  getAllTasks: () => [...get().tasks.values()].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
}))

// Helper to generate unique task IDs
export const generateTaskId = (type: TaskType): string => {
  return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}
